import {
  ESSENTIALS_FEATURE_FLAG,
  MODERN_MONITORING_TEMPLATE_FORM_FEATURE_FLAG,
  getEffectiveDefaultPresetId
} from '@kentik/ui-shared/nms/deviceMonitoringTemplates';

type CollectionMethod = string | string[] | null | undefined;

interface TemplateModel {
  id: number | string;
  isPreset: boolean;
  get: (key: string) => unknown;
}

interface TemplatesCollection {
  hasFetched: boolean;
  loading: boolean;
  models: TemplateModel[];
  defaultFlowTemplate?: { id: number | string };
  fetch: () => Promise<unknown>;
}

interface Auth {
  hasPermission: (permission: string, options?: { overrideForSudo?: boolean }) => boolean;
}

interface Form {
  getValue: (name: string) => unknown;
  setValue: (name: string, value: unknown) => void;
}

const getMethods = (method: CollectionMethod): string[] =>
  Array.isArray(method) ? method : (method?.split(',') ?? []);
const isFlowOnly = (method: CollectionMethod) => getMethods(method).includes('kentik_agent_flow_only');
const isMonitoredByUA = (method: CollectionMethod) =>
  getMethods(method).some((m) => m === 'kentik_agent' || m === 'kentik_agent_flow_only');

function findDefaultTemplate({
  collection,
  $auth,
  flowOnly
}: {
  collection: TemplatesCollection;
  $auth: Auth;
  flowOnly: boolean;
}) {
  if (flowOnly) {
    return collection.defaultFlowTemplate;
  }

  const useEssentials =
    $auth.hasPermission(MODERN_MONITORING_TEMPLATE_FORM_FEATURE_FLAG, { overrideForSudo: false }) ||
    $auth.hasPermission(ESSENTIALS_FEATURE_FLAG, { overrideForSudo: false });
  const presetId = getEffectiveDefaultPresetId(useEssentials);
  return collection.models.find(
    (template) => template.isPreset && template.get('preset') === presetId && template.get('template_type') !== 'FLOW'
  );
}

/**
 * When a UA-based collection method is selected (or already set on mount) and no monitoring profile is set,
 * preselect the default one for that method (NMS vs Flow). Existing selections are left alone.
 */
export default async function assignDefaultMonitoringTemplate({
  form,
  $auth,
  collection,
  collectionMethod
}: {
  form: Form;
  $auth: Auth;
  collection: TemplatesCollection;
  collectionMethod: CollectionMethod;
}): Promise<void> {
  if (!isMonitoredByUA(collectionMethod) || form.getValue('monitoring_template_id')) {
    return;
  }

  // hasFetched flips as soon as a fetch starts, so also wait on an in-flight one (fetch() reuses it).
  if (!collection.hasFetched || collection.loading) {
    try {
      await collection.fetch();
    } catch {
      return;
    }
  }

  // The user may have changed the method or picked a profile while templates were loading.
  const currentMethod = form.getValue('snmp_collection_method') as CollectionMethod;
  if (
    !isMonitoredByUA(currentMethod) ||
    isFlowOnly(currentMethod) !== isFlowOnly(collectionMethod) ||
    form.getValue('monitoring_template_id')
  ) {
    return;
  }

  const defaultTemplate = findDefaultTemplate({ collection, $auth, flowOnly: isFlowOnly(collectionMethod) });
  if (defaultTemplate) {
    form.setValue('monitoring_template_id', defaultTemplate.id);
  }
}
